import { useWorkspaceCloses } from "@api/close.api";
import {
    sortCloseStagesByOrder,
    useOrganizationCloseStages,
} from "@api/organizationCloseStage.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import {
    type TaskPriority as TaskPriorityEnum,
    type TaskStatus as TaskStatusEnum,
    UI_VISIBLE_TASK_STATUSES,
} from "@common/types/task";
import { UserAvatar } from "@components/avatar/UserAvatar";
import type { DataGridListOption } from "@design-system/DataGrid";
import type { PickerStatus } from "@design-system/Picker/Picker.lib";
import { useEntitiesById } from "@hooks/useEntities";
import { TaskPriority } from "../TaskPrioritySelect/TaskPriority";
import { ORDERED_TASK_PRIORITIES } from "../TaskPrioritySelect/TaskPrioritySelect";
import { TASK_PRIORITY_CONFIG } from "../TaskPrioritySelect/taskPriority.constant";
import { TASK_STATUS_CONFIG } from "../TaskStatusSelect/taskStatus.constants";
import type { NestedTaskRow } from "../types";
import { getCloseOptionDescription } from "../taskClose";

interface TaskPickerOptions<TOption extends string | number> {
    options: ReadonlyArray<DataGridListOption<TOption>>;
    status: PickerStatus;
}

const toPickerStatus = (query: {
    isLoading: boolean;
    isLoadingError: boolean;
}): PickerStatus => {
    if (query.isLoadingError) {
        return "error";
    }
    return query.isLoading ? "loading" : "idle";
};

const matchesSearch = ({ label, search }: { label: string; search: string }) =>
    label.toLowerCase().includes(search.trim().toLowerCase());

const searchOptions = <TOption extends string | number>({
    options,
    search,
}: {
    options: ReadonlyArray<DataGridListOption<TOption>>;
    search: string;
}) => options.filter(({ label }) => matchesSearch({ label, search }));

export const STATUS_OPTIONS: ReadonlyArray<DataGridListOption<TaskStatusEnum>> =
    UI_VISIBLE_TASK_STATUSES.map((status) => ({
        value: status,
        label: TASK_STATUS_CONFIG[status].tooltip,
    }));

export const PRIORITY_OPTIONS: ReadonlyArray<
    DataGridListOption<TaskPriorityEnum>
> = ORDERED_TASK_PRIORITIES.map((priority) => ({
    value: priority,
    label: TASK_PRIORITY_CONFIG[priority].label,
    leading: <TaskPriority priority={priority} variant="iconOnly" />,
}));

/** The row's workspace members, loaded once the Assignee picker opens. */
export const useAssigneeOptions = (
    _row: NestedTaskRow,
    search: string,
): TaskPickerOptions<number> => {
    const query = useCurrentWorkspaceMembers();
    const options = (query.data ?? []).map((member) => ({
        value: member.user.id,
        label: member.user.name,
        leading: <UserAvatar user={member.user} size="xs" aria-hidden />,
    }));
    return {
        options: searchOptions({ options, search }),
        status: toPickerStatus(query),
    };
};

/** The row's workspace task groups, loaded once the Group picker opens. */
export const useGroupOptions = (
    _row: NestedTaskRow,
    search: string,
): TaskPickerOptions<string> => {
    const query = useWorkspaceTaskGroups();
    const options = (query.data ?? []).map((group) => ({
        value: group.id,
        label: group.name,
    }));
    return {
        options: searchOptions({ options, search }),
        status: toPickerStatus(query),
    };
};

/** The row's workspace services, each with its entity and dates. */
export const useServiceOptions = (
    _row: NestedTaskRow,
    search: string,
): TaskPickerOptions<string> => {
    const query = useWorkspaceCloses();
    const entitiesById = useEntitiesById();
    const options = (query.data ?? []).map((close) => ({
        value: close.id,
        label: close.name,
        description: getCloseOptionDescription({
            entityName: close.entityId
                ? (entitiesById.get(close.entityId)?.name ?? undefined)
                : undefined,
            startDate: close.startDate,
            endDate: close.endDate,
        }),
    }));
    return {
        options: searchOptions({ options, search }),
        status: toPickerStatus(query),
    };
};

/** The organization's close stages in their order. */
export const useStageOptions = (
    _row: NestedTaskRow,
    search: string,
): TaskPickerOptions<string> => {
    const query = useOrganizationCloseStages();
    const options = sortCloseStagesByOrder(query.data ?? []).map((stage) => ({
        value: stage.id,
        label: stage.name,
    }));
    return {
        options: searchOptions({ options, search }),
        status: toPickerStatus(query),
    };
};
